import { useCallback, useEffect, useState } from 'react';

import {
  listOrganizations, createOrganization, listMemberships, grantMembership, revokeMembership,
  listVenueDirectory, rolesForScope, MEMBERSHIP_ROLES,
  type Organization, type Membership, type MembershipRole, type DirectoryVenue,
} from '../../lib/tenancy';
import { listUsers, type AdminUser } from '../../lib/admin';
import { ApiError } from '../../lib/apiClient';
import { usePageChrome } from '../../admin/pageChrome';
import { useCopy } from '../../copy/CopyProvider';
import './AdminPages.css';

/**
 * Organizations and memberships.
 *
 * Task 5 shipped this model in shadow mode and deferred its UI until the Admin shell
 * existed, which is now. This is the minimum that makes the model administrable: see the
 * organizations, create one, see who belongs to each, grant a membership, revoke one.
 *
 * ── THE SERVER IS AUTHORITATIVE, AND THIS SCREEN SAYS SO ─────────────────────────────
 *
 * Nothing here grants a power. Every action is a request the engine validates again:
 * it resolves the scope id against the table its type names, it rejects a role outside
 * memberships_roles_allowed_ck, and it refuses 'admin' anywhere but platform scope. The
 * role list below mirrors those rules so the UI does not offer what the server will
 * refuse — mirroring, not enforcing.
 *
 * ── A ROLE IS NOT AN ELIGIBILITY ─────────────────────────────────────────────────────
 *
 * Granting 'investor' says who somebody is. It approves nobody for a securities offering:
 * accreditation, sophistication and 506(b) network membership are separate records behind
 * separate gates, and no membership touches any of them. The screen states this where the
 * role is chosen, because the opposite assumption is the dangerous one.
 */

function RoleChecklist({
  scopeType, selected, onChange,
}: {
  scopeType: 'platform' | 'organization';
  selected: MembershipRole[];
  onChange: (roles: MembershipRole[]) => void;
}) {
  const allowed = rolesForScope(scopeType);
  return (
    <fieldset className="org-roles">
      <legend className="field__label">Roles</legend>
      <div className="org-roles__grid">
        {MEMBERSHIP_ROLES.map((role) => {
          const permitted = allowed.includes(role);
          return (
            <label
              key={role}
              className={`org-roles__item${permitted ? '' : ' is-disabled'}`}
              title={permitted ? undefined : 'Platform scope only.'}
            >
              <input
                type="checkbox"
                checked={selected.includes(role)}
                disabled={!permitted}
                onChange={(e) => onChange(
                  e.target.checked
                    ? [...selected, role]
                    : selected.filter((r) => r !== role),
                )}
              />
              <span>{role.replace(/_/g, ' ')}</span>
            </label>
          );
        })}
      </div>
      {selected.includes('investor') && (
        <p className="org-roles__note" role="note">
          The investor role is an identity, not an approval. It grants no access to any
          offering: accreditation and 506(b) eligibility are decided in Investor approvals.
        </p>
      )}
    </fieldset>
  );
}

function GrantForm({
  organization, users, onGranted,
}: {
  organization: Organization;
  users: AdminUser[];
  onGranted: () => void;
}) {
  const [userId, setUserId] = useState('');
  const [roles, setRoles] = useState<MembershipRole[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!userId || roles.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      await grantMembership({
        userId, scopeType: 'organization', scopeId: organization.id, roles,
      });
      setUserId(''); setRoles([]);
      onGranted();
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'Could not grant that membership.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="org-grant">
      {error && <p className="alert alert--error" role="alert">{error}</p>}
      <label className="field">
        <span className="field__label">User</span>
        <select className="field__input" value={userId} onChange={(e) => setUserId(e.target.value)}>
          <option value="">Choose a user…</option>
          {users.map((u) => (
            <option key={u.userId} value={u.userId}>
              {u.email}{u.role ? ` — ${u.role}` : ''}
            </option>
          ))}
        </select>
      </label>

      <RoleChecklist scopeType="organization" selected={roles} onChange={setRoles} />

      <button
        type="button"
        className="button button--primary"
        disabled={busy || !userId || roles.length === 0}
        onClick={() => void submit()}
      >
        Grant membership
      </button>
    </div>
  );
}

function OrganizationCard({ organization, users }: { organization: Organization; users: AdminUser[] }) {
  const [members, setMembers] = useState<Membership[] | null>(null);
  const [revoked, setRevoked] = useState<Membership[]>([]);
  const [venues, setVenues] = useState<DirectoryVenue[]>([]);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    listMemberships({ scopeType: 'organization', scopeId: organization.id })
      .then(setMembers)
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : 'Could not load memberships.'));
    listMemberships({ scopeType: 'organization', scopeId: organization.id, status: 'revoked' })
      .then(setRevoked).catch(() => setRevoked([]));
  }, [organization.id]);

  useEffect(() => {
    if (!open) return;
    load();
    listVenueDirectory({ organizationId: organization.id }).then(setVenues).catch(() => setVenues([]));
  }, [open, load, organization.id]);

  async function revoke(membership: Membership) {
    const who = membership.userEmail ?? 'this user';
    // A revoke takes access away from a real person; it is worth one deliberate beat.
    if (!window.confirm(`Revoke ${membership.roles.join(', ')} for ${who} in ${organization.name}?`)) {
      return;
    }
    setError(null);
    try {
      await revokeMembership(membership.id);
      load();
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'Could not revoke that membership.');
    }
  }

  return (
    <article className="net-panel">
      <div className="spc-head">
        <div>
          <h2 className="net-panel__title">{organization.name}</h2>
          <p className="net-panel__lede mono">{organization.slug}</p>
        </div>
        <div className="net-row__badges">
          <span className={`badge ${organization.status === 'active' ? 'badge--ok' : 'badge--muted'}`}>
            {organization.status}
          </span>
          <span className="badge badge--muted">
            {organization.memberCount ?? 0} member{organization.memberCount === 1 ? '' : 's'}
          </span>
          {open && (
            <span className="badge badge--muted">
              {venues.length} venue{venues.length === 1 ? '' : 's'}
            </span>
          )}
        </div>
      </div>

      <button
        type="button"
        className="button button--ghost adm-btn-sm"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? 'Hide members' : 'Manage members'}
      </button>

      {open && (
        <div className="org-members">
          {error && <p className="alert alert--error" role="alert">{error}</p>}

          {!members && <p className="adm-lede">Loading members…</p>}
          {members && members.length === 0 && (
            <p className="adm-lede">Nobody has a membership in this organization yet.</p>
          )}

          {(members ?? []).map((m) => (
            <div className="org-member" key={m.id}>
              <div>
                <span className="net-row__email">{m.userEmail ?? m.userId}</span>
                <div className="net-row__badges">
                  {m.roles.map((r) => (
                    <span className="badge badge--network" key={r}>{r.replace(/_/g, ' ')}</span>
                  ))}
                </div>
              </div>
              <button type="button" className="button button--ghost adm-btn-sm"
                onClick={() => void revoke(m)}>
                Revoke
              </button>
            </div>
          ))}

          {revoked.length > 0 && (
            <details className="org-revoked">
              <summary>{revoked.length} revoked membership{revoked.length === 1 ? '' : 's'}</summary>
              {revoked.map((m) => (
                <div className="org-member is-revoked" key={m.id}>
                  <span className="net-row__email">{m.userEmail ?? m.userId}</span>
                  <div className="net-row__badges">
                    {m.roles.map((r) => (
                      <span className="badge badge--muted" key={r}>{r.replace(/_/g, ' ')}</span>
                    ))}
                    <span className="badge badge--muted">revoked</span>
                  </div>
                </div>
              ))}
            </details>
          )}

          <GrantForm organization={organization} users={users} onGranted={load} />
        </div>
      )}
    </article>
  );
}

export function AdminOrganizations() {
  const t = useCopy();
  const [orgs, setOrgs] = useState<Organization[] | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    listOrganizations()
      .then(setOrgs)
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : 'Could not load organizations.'));
  }, []);

  useEffect(() => {
    load();
    listUsers().then(setUsers).catch(() => setUsers([]));
  }, [load]);

  usePageChrome({
    title: t('admin.organizations.title', 'Organizations'),
    subtitle: t('admin.organizations.subtitle', 'Tenants, and who belongs to them. '
      + 'Memberships are additive: they grant access, never remove it.'),
  }, [t]);

  async function create() {
    if (!name.trim()) return;
    setCreating(true);
    setError(null);
    try {
      await createOrganization({ name: name.trim() });
      setName('');
      load();
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'Could not create that organization.');
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="adm-page">
      {error && <div className="alert alert--error" role="alert">{error}</div>}

      <section className="net-panel">
        <h2 className="net-panel__title">New organization</h2>
        <div className="org-create">
          <label className="field">
            <span className="field__label">Name</span>
            <input className="field__input" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Vyrtual Productions" />
          </label>
          <button type="button" className="button button--primary"
            disabled={creating || !name.trim()} onClick={() => void create()}>
            Create
          </button>
        </div>
      </section>

      {!orgs && !error && <p className="adm-lede">Loading organizations…</p>}
      {orgs && orgs.length === 0 && <p className="adm-lede">No organizations yet.</p>}

      <div className="adm-list">
        {(orgs ?? []).map((org) => (
          <OrganizationCard key={org.id} organization={org} users={users} />
        ))}
      </div>
    </div>
  );
}
